import { useState } from "react";
import {
  Alert, Box, Button, Chip, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { journalsApi, type JournalEntryListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const STATUS = ["", "Draft", "Submitted", "Approved", "Posted", "Cancelled"];
const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};
const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/**
 * UC-M1-ACC-01 — Journal entries list. Manual + auto-generated (PI/SI/Adjustment)
 * journals show here; filter by status, reference type, or date range.
 */
export function JournalsListPage() {
  const navigate = useNavigate();
  const canCreate = useAuth(s => s.hasPermission("accounting.journal.create"));
  const [status, setStatus] = useState("");
  const [referenceType, setReferenceType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  const query = useQuery({
    queryKey: ["journal-entries", status, referenceType, fromDate, toDate, pagination.page, pagination.pageSize],
    queryFn: () => journalsApi.list({
      status: status || undefined,
      referenceType: referenceType || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<JournalEntryListDto>[] = [
    { field: "entryNo", headerName: "Entry #", width: 180 },
    { field: "entryDate", headerName: "Date", width: 110,
      valueFormatter: (v) => v ? new Date(v as string).toLocaleDateString() : "" },
    { field: "referenceType", headerName: "Source", width: 140,
      renderCell: (p) => p.row.isAutoGenerated
        ? <Chip size="small" variant="outlined" label={p.value ?? "auto"} />
        : <Chip size="small" variant="outlined" color="primary" label="Manual" /> },
    { field: "description", headerName: "Description", flex: 1, minWidth: 240 },
    { field: "status", headerName: "Status", width: 120,
      renderCell: (p) => <Chip size="small" label={p.value} color={STATUS_COLOR[p.value as string] ?? "default"} /> },
    { field: "totalDebit", headerName: "Debit", width: 140, type: "number",
      valueFormatter: (v) => inr.format((v as number) ?? 0) },
    { field: "totalCredit", headerName: "Credit", width: 140, type: "number",
      valueFormatter: (v) => inr.format((v as number) ?? 0) },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Journal entries</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => navigate("/accounting/journals/new")}>
            New manual journal
          </Button>
        )}
      </Stack>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
            size="small" sx={{ minWidth: 140 }}>
            {STATUS.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
          <TextField label="Source ref" size="small" sx={{ minWidth: 160 }}
            value={referenceType} onChange={(e) => setReferenceType(e.target.value)}
            placeholder="PI / SI / Adjustment" />
          <TextField type="date" label="From" size="small" InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <TextField type="date" label="To" size="small" InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            paginationMode="server" rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination} onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]} disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(p) => navigate(`/accounting/journals/${p.id}`)}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
